import type { SseMessage, SsePort } from "../contracts";
import { parseSseBuffer } from "./sse-parse";

// Fetch-based SSE (EventSource can't send an Authorization header, the backend
// stream is bearer-authed). Pure-ish: depends only on fetch + web streams, both
// injectable. Owns ONE connection with auto-reconnect — the central EventManager
// is the only thing that constructs it.
export interface SseClientOptions {
    baseUrl?: string;
    getAccessToken?: () => string | null;
    fetchFn?: typeof fetch;
    reconnectDelayMs?: number;
}

export class SseClient implements SsePort {
    private readonly baseUrl: string;
    private readonly getAccessToken: () => string | null;
    private readonly fetchFn: typeof fetch;
    private readonly reconnectDelayMs: number;

    private readonly handlers = new Set<(message: SseMessage) => void>();
    private controller: AbortController | null = null;
    private closed = true;
    private buffer = "";

    constructor(opts: SseClientOptions = {}) {
        this.baseUrl = opts.baseUrl ?? "";
        this.getAccessToken = opts.getAccessToken ?? (() => null);
        this.fetchFn = opts.fetchFn ?? globalThis.fetch.bind(globalThis);
        this.reconnectDelayMs = opts.reconnectDelayMs ?? 3000;
    }

    connect(path: string): void {
        if (!this.closed) return;
        this.closed = false;
        void this.openStream(path);
    }

    disconnect(): void {
        this.closed = true;
        this.controller?.abort();
        this.controller = null;
        this.buffer = "";
    }

    onMessage(handler: (message: SseMessage) => void): () => void {
        this.handlers.add(handler);
        return () => this.handlers.delete(handler);
    }

    private async openStream(path: string): Promise<void> {
        while (!this.closed) {
            this.controller = new AbortController();
            try {
                const token = this.getAccessToken();
                const response = await this.fetchFn(`${this.baseUrl}${path}`, {
                    headers: {
                        Accept: "text/event-stream",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    signal: this.controller.signal,
                });
                if (!response.body) throw new Error("SSE response has no body");

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                for (;;) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    this.buffer += decoder.decode(value, { stream: true });
                    const { messages, rest } = parseSseBuffer(this.buffer);
                    this.buffer = rest;
                    for (const message of messages) this.dispatch(message);
                }
            } catch {
                // network error / abort → fall through to reconnect unless closed
            }
            this.buffer = "";
            if (this.closed) break;
            await delay(this.reconnectDelayMs);
        }
    }

    private dispatch(message: SseMessage): void {
        for (const handler of [...this.handlers]) handler(message);
    }
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
